/** Copyright (c) 2017 T. Rowe Price. All rights reserved. */

/* eslint-disable no-global-assign */ // location should normally not be modified, but we make an exception in this case

import './SessionTimer.scss';

import classnames from 'classnames';
import { isEmpty, isString, uniq } from 'lodash';
import React from 'react';
import { connect } from 'react-redux';
import { getProducts } from 'services/Products';
import { getAccounts } from 'state/account/AccountSelectors';
import { getView } from 'state/view/ViewSelectors';

import BaseModal from 'components/shared/Modal/BaseModal';

import {
  LAST_REQUEST_TIME,
  LOGGED_IN_FLAG,
  TIMED_OUT_FLAG
} from 'services/Auth';
import { GLOBALS } from 'services/globals';

import { logoutUrl, timeoutUrl } from 'services/Externals';

import { pingServer } from 'services/Rest';

class SessionTimer extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      showModal: false,
      timeRemaining: {},
      countdownTime: GLOBALS.SESSION_TIMEOUT_MINUTES * 60
    };

    localStorage.setItem(LOGGED_IN_FLAG, true);
    localStorage.setItem(TIMED_OUT_FLAG, false);

    this.logOut = this.logOut.bind(this);
    this.refreshSession = this.refreshSession.bind(this);
  }

  componentDidMount() {
    this.timer = setInterval(() => {
      const products = uniq(getProducts(this.props.accounts));
      if (JSON.parse(localStorage.getItem(TIMED_OUT_FLAG))) {
        this.timeout(products);
      } else if (!JSON.parse(localStorage.getItem(LOGGED_IN_FLAG))) {
        this.logOut();
      }

      let { lastRequestTimeMilliseconds } = this.props;
      const { countdownTime, showModal } = this.state;

      if (
        localStorage.getItem(LAST_REQUEST_TIME) > lastRequestTimeMilliseconds
      ) {
        lastRequestTimeMilliseconds = localStorage.getItem(LAST_REQUEST_TIME);
      }

      const timeSinceLastRequestSeconds = Math.ceil(
        (Date.now() - lastRequestTimeMilliseconds) / 1000
      );
      const timeUntilExpireSeconds =
        GLOBALS.SESSION_TIMEOUT_MINUTES * 60 - timeSinceLastRequestSeconds;

      this.setState((prevState) => ({
        countdownTime:
          timeUntilExpireSeconds > prevState.countdownTime
            ? timeUntilExpireSeconds
            : prevState.countdownTime - 1
      }));

      const minutes = Math.floor((countdownTime % (60 * 60)) / 60);
      const seconds = Math.ceil((countdownTime % (60 * 60)) % 60);

      this.setState({
        timeRemaining: {
          minutes: minutes,
          seconds: seconds
        }
      });

      if (
        countdownTime < GLOBALS.DISPLAY_TIMEOUT_WARNING_MINUTES * 60 &&
        !showModal
      ) {
        this.setState({ showModal: true });
      } else if (
        showModal &&
        countdownTime > GLOBALS.DISPLAY_TIMEOUT_WARNING_MINUTES * 60
      ) {
        this.setState({ showModal: false });
      }

      if (minutes === 0 && seconds === 0) {
        localStorage.removeItem(LAST_REQUEST_TIME);
        clearInterval(this.timer);
        this.timeout(products);
      }
    }, 1000);
  }

  componentWillUnmount() {
    clearInterval(this.timer);
  }

  timeout(products) {
    if (!JSON.parse(localStorage.getItem(TIMED_OUT_FLAG))) {
      localStorage.setItem(TIMED_OUT_FLAG, true);
    }

    this.setState({ showModal: false });
    clearInterval(this.timer);
    location = timeoutUrl(products);
  }

  logOut() {
    if (JSON.parse(localStorage.getItem(LOGGED_IN_FLAG))) {
      localStorage.setItem(LOGGED_IN_FLAG, false);
    }

    this.setState({ showModal: false });
    clearInterval(this.timer);
    const products = uniq(getProducts(this.props.accounts));
    location = logoutUrl(products);
  }

  refreshSession() {
    this.setState(() => ({
      countdownTime: GLOBALS.SESSION_TIMEOUT_MINUTES * 60
    })); // Prevent a delay in hiding the modal
    pingServer();
    this.setState({ showModal: false });
  }

  renderTime() {
    const { timeRemaining } = this.state;
    const { minutes, seconds } = timeRemaining;

    const minutesDisplay = minutes < 10 ? `0${minutes}` : minutes;
    const secondsDisplay = seconds < 10 ? `0${seconds}` : seconds;

    return minutes > 0
      ? `${minutesDisplay}:${secondsDisplay} minutes`
      : `${minutesDisplay}:${secondsDisplay} seconds`;
  }

  render() {
    const { showModal } = this.state;
    const { customer, view } = this.props;

    const name =
      isString(customer.fullName) && !isEmpty(customer.fullName)
        ? `${customer.fullName.split(' ').shift()}, we`
        : 'We';

    const sessionModalClassName = classnames(
      'session-timeout-modal-container',
      {
        hideModal: !showModal
      }
    );

    return (
      <BaseModal
        className={sessionModalClassName}
        close={this.refreshSession}
        title="You are about to be logged out"
        isOpen={showModal}
        hideAnchor={true}
        hideClose={true}
        disableClickOff={true}>
        <div className="session-timer-container">
          <div className="session-timer-message">
            <p className="warning">
              {name} have noticed that you have been inactive for a while. For
              your security, we&apos;ll automatically log you out in
              approximately{' '}
              <span className="session-timer-counter">
                {this.renderTime()}.
              </span>
            </p>
            <p className="button-disclosure">
              Click &quot;Keep Me Online&quot; to continue using your online
              accounts. If you&apos;re done, you may click &quot;Log Out&quot;.
            </p>
          </div>
          <div className="session-timer-options">
            <button className="logout" onClick={() => this.logOut(view)}>
              Log Out
            </button>
            <button className="keep-session" onClick={this.refreshSession}>
              Keep Me Online
            </button>
          </div>
        </div>
      </BaseModal>
    );
  }
}

const mapStateToProps = (state) => {
  const { customer, lastRequestTime } = state;
  return {
    accounts: getAccounts(state),
    customer,
    lastRequestTimeMilliseconds: lastRequestTime,
    view: getView(state)
  };
};

export default connect(mapStateToProps)(SessionTimer);
